import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import './SearchModal.css';

const SearchModal = ({ isOpen, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [recentSearches, setRecentSearches] = useState([
    'Spider-Man',
    'Kung Fu Panda',
    'Oppenheimer',
    'Justice League'
  ]);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      onClose();
    }
  };

  const handleRecentSearch = (term) => {
    setSearchQuery(term);
    navigate(`/search?q=${encodeURIComponent(term)}`);
    onClose();
  };

  const modalVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  const contentVariants = {
    hidden: { opacity: 0, y: -50 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        delay: 0.1,
        duration: 0.3,
      }
    },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          className="search-modal"
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
        >
          <div className="search-modal__backdrop" onClick={onClose} />
          
          <motion.div 
            className="search-modal__content"
            variants={contentVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
          >
            <button 
              className="search-modal__close"
              onClick={onClose}
              aria-label="Close search"
            >
              <CloseIcon fontSize="large" />
            </button>

            <div className="search-modal__container">
              <form onSubmit={handleSearch} className="search-modal__form">
                <div className="search-modal__input-wrapper">
                  <SearchIcon className="search-modal__input-icon" />
                  <input
                    type="text"
                    className="search-modal__input"
                    placeholder="Search for movies, TV series..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    autoFocus
                  />
                </div>
              </form>

              <motion.div 
                className="search-modal__recent"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <h3 className="search-modal__recent-title">Recent Searches</h3>
                <div className="search-modal__tags">
                  {recentSearches.map((term, index) => (
                    <motion.button
                      key={term}
                      className="search-modal__tag"
                      onClick={() => handleRecentSearch(term)}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.3 + index * 0.05 }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {term}
                    </motion.button>
                  ))}
                </div>
              </motion.div>

              <motion.div 
                className="search-modal__suggestions"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                <h3 className="search-modal__suggestions-title">Trending Now</h3>
                <div className="search-modal__trending">
                  {['Avatar 2', 'The Batman', 'Top Gun: Maverick', 'Black Panther 2'].map((movie, index) => (
                    <motion.div
                      key={movie}
                      className="search-modal__trending-item"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.6 + index * 0.1 }}
                      whileHover={{ x: 10 }}
                      onClick={() => handleRecentSearch(movie)}
                    >
                      <span className="search-modal__trending-number">{index + 1}</span>
                      <span className="search-modal__trending-title">{movie}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SearchModal;